"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildRegistrationZodSchema = buildRegistrationZodSchema;
/**
 * ADR-034 Decision 9 — `packages/contracts`'s first runtime-logic
 * artifact (everything else here is schemas/DTOs/permission strings).
 * One small, framework-agnostic function, shared identically by the
 * frontend's dynamically-rendered registration form (`useForm`'s
 * resolver) and the backend's Compliance Check
 * (`PatientRegistrationApplicationService`) — genuinely the same logic
 * needed in both places, the strongest case for putting logic in this
 * package. Deliberately NOT smuggled in silently: flagged here and in
 * the ADR as a deliberate first, not something to be surprised by later.
 *
 * Builds a `z.object({...})` at RUNTIME from a resolved field list
 * (`registration-config-resolver.service.ts`'s output) rather than a
 * compile-time literal — this is what replaces the old "one static
 * Zod schema per registration mode, shared frontend+backend" approach
 * now that the field list itself is admin-configurable.
 *
 * Two rule types are intentionally NOT evaluated here, always passing:
 * `UNIQUE` (needs a real, async DB round-trip — this compiler only
 * builds the synchronous SHAPE validator) and `CUSTOM` (no generic
 * interpreter for arbitrary custom logic exists this phase — there is
 * no named-custom-rule registry to resolve against). Both are
 * disclosed limitations, not silent gaps — see ADR-034.
 */
const zod_1 = require("zod");
function isEmptyValue(value) {
    return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
}
function evaluateValidationRule(ruleType, configJson, value) {
    const config = (configJson ?? {});
    switch (ruleType) {
        case 'REGEX':
        case 'GOVERNMENT_ID_VALIDATION': {
            const pattern = typeof config.pattern === 'string' ? config.pattern : undefined;
            return pattern ? new RegExp(pattern).test(String(value)) : true;
        }
        case 'MIN_LENGTH': {
            const min = typeof config.min === 'number' ? config.min : 0;
            return String(value).length >= min;
        }
        case 'MAX_LENGTH': {
            const max = typeof config.max === 'number' ? config.max : Number.POSITIVE_INFINITY;
            return String(value).length <= max;
        }
        case 'AGE_VALIDATION': {
            const minAge = typeof config.minAge === 'number' ? config.minAge : undefined;
            const maxAge = typeof config.maxAge === 'number' ? config.maxAge : undefined;
            const dob = new Date(value);
            if (Number.isNaN(dob.getTime()))
                return false;
            const ageYears = (Date.now() - dob.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
            if (minAge !== undefined && ageYears < minAge)
                return false;
            if (maxAge !== undefined && ageYears > maxAge)
                return false;
            return true;
        }
        case 'PHONE_VALIDATION':
            return /^\+?[0-9]{7,15}$/.test(String(value));
        case 'EMAIL_VALIDATION':
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value));
        case 'UNIQUE':
        case 'CUSTOM':
        case 'REQUIRED':
        default:
            return true;
    }
}
/**
 * `isAlwaysRequired` (ADR-034 Decision 5) can never be loosened by any
 * configuration — it always wins over `requirement`. A HIDDEN field is
 * never required to be filled regardless of `requirement`/
 * `isAlwaysRequired`, since there is no rendered input for the user to
 * fill it with.
 */
function buildRegistrationZodSchema(resolvedFields) {
    const shape = {};
    for (const field of resolvedFields) {
        if (field.visibility === 'HIDDEN') {
            shape[field.fieldCode] = zod_1.z.unknown().optional();
            continue;
        }
        const required = field.isAlwaysRequired || field.requirement === 'REQUIRED';
        shape[field.fieldCode] = zod_1.z.unknown().superRefine((value, ctx) => {
            if (isEmptyValue(value)) {
                if (required) {
                    ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: `${field.fieldName} is required` });
                }
                return;
            }
            for (const rule of field.validationRules) {
                if (!evaluateValidationRule(rule.ruleType, rule.configJson, value)) {
                    ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: rule.errorMessage });
                }
            }
        });
    }
    return zod_1.z.object(shape);
}
//# sourceMappingURL=registration-config-compiler.js.map